import React from 'react';
import {
  TableCell,
  TableRow,
  Box,
  Table,
  TableHead,
  TableBody,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

interface ChartDataTableProps {
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string;
    }>;
  };
  tabKey: string;
}

const COLUMN_HEADERS: Record<string, string> = {
  agreements: 'statistics.table.agreement',
  topSearches: 'statistics.table.searchTerm',
  noResults: 'statistics.table.searchTerm',
  accountActivity: 'statistics.table.account',
};

export const ChartDataTable: React.FC<ChartDataTableProps> = ({
  data,
  tabKey,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!data?.labels || !data?.datasets) return null;

  const tableData = data.labels.map((label, index) => ({
    label,
    values: data.datasets.map((dataset) => dataset.data[index] || 0),
  }));

  const getTableCaption = () => {
    const datasetSummary = data.datasets
      .map(
        (ds) =>
          `${ds.label}: ${ds.data
            .reduce((sum, val) => sum + val, 0)
            .toLocaleString()}`
      )
      .join(', ');
    return t(
      'statistics.table.caption',
      `Data from ${data.labels[0]} to ${
        data.labels[data.labels.length - 1]
      }. Totals: ${datasetSummary}`
    );
  };

  return (
    <Box
      aria-label={String(
        t(`statistics.${tabKey}.tableRegion`, `${tabKey} statistics`)
      )}
      tabIndex={0}
      sx={{
        mt: 24,
        maxHeight: 400,
        overflowY: 'auto',
        overflowX: 'hidden',
        border: '1px solid var(--color-greyish-blue)',
        borderBottomLeftRadius: '6px',
        borderBottomRightRadius: '6px',
      }}
    >
      <Table
        stickyHeader
        size="small"
        aria-label={String(
          t(`statistics.${tabKey}.tableLabel`, `${tabKey} data`)
        )}
        sx={{
          maxHeight: 400,
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          },
          '& thead th': {
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: 1.2,
            px: { xs: 4, sm: 16 },
            py: { xs: 2, sm: 6 },
          },
          '& tbody td': {
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: 1.2,
            px: { xs: 4, sm: 16 },
            py: { xs: 2, sm: 6 },
          },
        }}
      >
        <caption className="sr-only">{getTableCaption()}</caption>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '35%' }}>
              {t(COLUMN_HEADERS[tabKey] || 'statistics.table.item', 'Item')}
            </TableCell>
            {data.datasets.map((dataset, index) => (
              <TableCell
                key={index}
                sx={{
                  color: dataset.backgroundColor || theme.palette.text.primary,
                  textAlign: 'end',
                }}
                aria-sort="none"
              >
                {dataset.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, rowIndex) => (
            <TableRow key={rowIndex} aria-rowindex={rowIndex + 2}>
              <TableCell
                component="th"
                scope="row"
                id={`${tabKey}-row-${rowIndex}`}
                sx={{ width: '35%' }}
              >
                {row.label}
              </TableCell>
              {row.values.map((value, colIndex) => (
                <TableCell
                  key={colIndex}
                  headers={`${tabKey}-row-${rowIndex}`}
                  aria-label={`${value.toLocaleString()} ${
                    data.datasets[colIndex].label
                  }`}
                  sx={{
                    textAlign: 'end',
                  }}
                >
                  {value.toLocaleString()}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
